import React, {useId} from 'react';
import {Button, CopyIcon, LayoutIcon} from '@taopix/taopix-design-system';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import {useTranslation} from 'react-i18next';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {useTaopix} from '../../Context/TaopixContext';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import {UserFeedbackActions} from '../../Context/UserFeedback/UserFeedbackActionTypes';
import {useUserFeedback} from '../../Context/UserFeedback/UserFeedbackContext';
import {ProductLayout, ReducerState} from '@taopix/taopix-core/dist/types/types';
import {useSelector} from 'react-redux';
import {LayoutSelector, TotalPageCountSelector} from '../../../Selectors';
import {getAddOrDeletePageNumber, onPageCountChanged} from '../../../Utils/Page';
import {NumBool, PageConstants} from '@taopix/taopix-core';

type DuplicatePageButtonProps = {
  pageNumber: number;
  buttonProps: ButtonProps;
  disabled?: boolean;
};

const DuplicatePageButton = ({pageNumber, buttonProps, disabled}: DuplicatePageButtonProps) => {
  const app = useTaopix();
  const {t} = useTranslation();
  const ui = useUiState();
  const userFeedback = useUserFeedback();

  const pageCount = useSelector(TotalPageCountSelector);
  const productLayout = useSelector(LayoutSelector);

  const buttonId = 'duplicatePageButton-' + useId().replaceAll(':', '');
  const label = productLayout.retroprints
    ? t('str_ButtonDuplicatePrint')
    : t('str_ButtonDuplicatePage');

  const onDuplicatePage = () => {
    if (ui.state.busy) return;
    ui.dispatch(UserInterfaceActions.setIsBusy(true));

    app.core
      .get('stateManager')
      .call<{}, ReducerState & {spineResized: boolean}>('addpages', {
        currentPage: pageNumber,
        method: 'insert',
        noPages: NumBool.True === productLayout.spreads ? 2 : 1,
        pageNumber: getAddOrDeletePageNumber(pageNumber, productLayout),
        duplicatePageContent: true,
      })
      .then(response => {
        onPageCountChanged(
          {spineResized: response.spineResized || false},
          ui,
          response?.project.productLayout as ProductLayout,
          pageCount
        );
        return response;
      })
      .catch(error => {
        let message = app.core.get('server').getErrorFromResponse(error);
        if ('' === message || !message) {
          message = error;
        }
        userFeedback.dispatch(
          UserFeedbackActions.setErrorUserFeedback({
            title: '',
            message,
            showHeader: true,
          })
        );
      })
      .finally(() => {
        ui.dispatch(UserInterfaceActions.setIsBusy(false));
        // Close the page actions palette if it is open
        if (ui.state.palette.section === 'pageActionsPalette') {
          ui.dispatch(UserInterfaceActions.setToolPaletteSection(null));
        }
      });
  };

  if ([PageConstants.FrontCoverPageNumber, PageConstants.BackCoverPageNumber].includes(pageNumber))
    return <></>;

  return (
    <>
      <Button
        id={buttonId}
        label={label}
        startIcon={<CopyIcon />}
        onClick={onDuplicatePage}
        disabled={disabled}
        {...buttonProps}
      />
    </>
  );
};
export default DuplicatePageButton;
